import {
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import type { ArchivedInspectionCaseSummary, InspectionCaseSummary } from './admin-api';
import { confirmAdminNavigation } from './admin-leave-guard';
import {
  bookingStatusLabel,
  caseStatusLabel,
  decisionLabel,
  formatYekaterinburg,
  snapshotText,
} from './admin-presentation';
import { StatusChip, statusToneClass } from './admin-ui';
import { navigate } from './path-router';

const PAGE_SIZE = 10;

function isPlainPrimaryClick(event: ReactMouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}

export function AdminCaseLink(props: {
  readonly href: string;
  readonly children: ReactNode;
  readonly className?: string;
}): ReactElement {
  return (
    <a
      href={props.href}
      className={props.className ?? 'pkg-c-case-link'}
      onClick={(event) => {
        if (!isPlainPrimaryClick(event)) {
          return;
        }
        event.preventDefault();
        if (!confirmAdminNavigation()) {
          return;
        }
        navigate(props.href);
      }}
    >
      {props.children}
    </a>
  );
}

export function caseContractorLabel(row: InspectionCaseSummary): string | null {
  const label = snapshotText(row.contractor, ['legal_name', 'name', 'display_name']);
  if (label === null || label.trim().length === 0) {
    return null;
  }
  return label.trim();
}

export function caseVehicleLabel(row: InspectionCaseSummary): string {
  return row.vehicle?.registration_number ?? row.vehicle?.vin_or_alternative_identifier ?? '—';
}

export function caseDetailHref(kind: 'inspections' | 'archive', caseId: string): string {
  return kind === 'archive' ? `/admin/archive/${caseId}` : `/admin/inspections/${caseId}`;
}

/** First-column cell: contractor hyperlink to THIS case, never contractor master. */
export function CaseContractorCell(props: {
  readonly row: InspectionCaseSummary;
  readonly kind: 'inspections' | 'archive';
}): ReactElement {
  const href = caseDetailHref(props.kind, props.row.id);
  const name = caseContractorLabel(props.row);
  if (name !== null) {
    return <AdminCaseLink href={href}>{name}</AdminCaseLink>;
  }
  if (props.row.id.trim().length > 0) {
    return <AdminCaseLink href={href}>Открыть осмотр</AdminCaseLink>;
  }
  return <span>Не указан</span>;
}

export function caseBookingStamp(row: InspectionCaseSummary): string {
  return `${bookingStatusLabel(row.booking.status)} · ${formatYekaterinburg(row.booking.scheduled_start_at)}`;
}

export function sortCasesByScheduledDesc(
  rows: readonly InspectionCaseSummary[],
): InspectionCaseSummary[] {
  return rows.slice().sort((left, right) => {
    const a = Date.parse(left.booking.scheduled_start_at);
    const b = Date.parse(right.booking.scheduled_start_at);
    const safeA = Number.isFinite(a) ? a : 0;
    const safeB = Number.isFinite(b) ? b : 0;
    if (safeB !== safeA) {
      return safeB - safeA;
    }
    return left.case_number.localeCompare(right.case_number, 'ru');
  });
}

export function matchCaseQuery(row: InspectionCaseSummary, query: string): boolean {
  const q = query.trim().toLocaleLowerCase('ru');
  if (q.length === 0) {
    return true;
  }
  const contractor = caseContractorLabel(row)?.toLocaleLowerCase('ru') ?? '';
  const plate = caseVehicleLabel(row).toLocaleLowerCase('ru');
  return (
    row.case_number.toLocaleLowerCase('ru').includes(q) ||
    plate.includes(q) ||
    contractor.includes(q)
  );
}

export function uniqueContractorOptions(
  rows: readonly InspectionCaseSummary[],
): readonly string[] {
  const set = new Set<string>();
  for (const row of rows) {
    const name = caseContractorLabel(row);
    if (name !== null) {
      set.add(name);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
}

export function useClientPage<T>(rows: readonly T[], pageSize: number = PAGE_SIZE): {
  page: number;
  setPage: (page: number) => void;
  pageRows: T[];
  totalPages: number;
  resetPage: () => void;
} {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize) as T[];
  }, [rows, safePage, pageSize]);
  return {
    page: safePage,
    setPage,
    pageRows,
    totalPages,
    resetPage: () => setPage(1),
  };
}

export function InspectionCasesTable(props: {
  readonly rows: readonly InspectionCaseSummary[];
  readonly kind: 'inspections' | 'archive';
  readonly showDecision?: boolean;
  readonly emptyLabel: string;
}): ReactElement {
  if (props.rows.length === 0) {
    return (
      <p role="status" className="empty-state">
        {props.emptyLabel}
      </p>
    );
  }
  const showDecision = props.showDecision === true;
  return (
    <div className="table-wrap pkg-c-table-wrap">
      <table className="data-table pkg-c-case-table">
        <thead>
          <tr>
            <th scope="col">Подрядчик</th>
            <th scope="col">Госномер</th>
            <th scope="col">Номер осмотра</th>
            <th scope="col">{showDecision ? 'Решение' : 'Статус'}</th>
            <th scope="col">{showDecision ? 'Дата записи' : 'Запись'}</th>
            <th scope="col">Действие</th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => {
            const href = caseDetailHref(props.kind, row.id);
            const archived = row as ArchivedInspectionCaseSummary;
            return (
              <tr key={row.id}>
                <td>
                  <CaseContractorCell row={row} kind={props.kind} />
                </td>
                <td>{caseVehicleLabel(row)}</td>
                <td>
                  <span className="pkg-c-case-number">{row.case_number}</span>
                </td>
                <td>
                  {showDecision ? (
                    archived.decision === 'APPROVED' || archived.decision === 'REJECTED' ? (
                      <StatusChip
                        label={decisionLabel(archived.decision)}
                        tone={statusToneClass(archived.decision)}
                      />
                    ) : (
                      <span className="meta">—</span>
                    )
                  ) : (
                    <StatusChip
                      label={caseStatusLabel(row.status)}
                      tone={statusToneClass(row.status)}
                    />
                  )}
                </td>
                <td>{formatYekaterinburg(row.booking.scheduled_start_at)}</td>
                <td>
                  <AdminCaseLink href={href}>Открыть</AdminCaseLink>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ClientPager(props: {
  readonly page: number;
  readonly totalPages: number;
  readonly total: number;
  readonly onPage: (page: number) => void;
  readonly scopeNote: string;
}): ReactElement | null {
  if (props.total === 0) {
    return null;
  }
  return (
    <div className="pkg-c-pager">
      <p className="meta">
        Показано на странице · всего в загруженной выборке: {props.total}. {props.scopeNote}
      </p>
      {props.totalPages > 1 ? (
        <div className="pkg-c-pager-controls">
          <button
            type="button"
            disabled={props.page <= 1}
            onClick={() => props.onPage(props.page - 1)}
          >
            Назад
          </button>
          <span className="meta">
            Стр. {props.page} / {props.totalPages}
          </span>
          <button
            type="button"
            disabled={props.page >= props.totalPages}
            onClick={() => props.onPage(props.page + 1)}
          >
            Вперёд
          </button>
        </div>
      ) : null}
    </div>
  );
}

export { PAGE_SIZE as OPS_CASE_PAGE_SIZE };
